import { AuthForbiddenError } from '@services/errors/AuthForbiddenError'
import axios, { AxiosError } from 'axios'
import {
  getRefreshToken,
  getTokenCookie,
  setRefreshTokenCookie,
  setTokenCookie
} from '@utils/auth-cookie'
import Router from 'next/router'
import { AuthtokenError } from '../errors/AuthTokenError'
import { signOut } from 'context/auth/auth-context'

let isRefreshing = false
let failedRequestQueue: {
  onSuccess: (token: string) => void
  onFailure: (err: AxiosError) => void
}[] = []

export const baseURL =
  typeof window !== 'undefined'
    ? process.env.NEXT_PUBLIC_BACKEND_BASE_URL
    : process.env.NEXT_CONTAINER_API_URL

export function setupAPIServer(ctx = undefined) {
  const token = getTokenCookie(ctx)

  const api = axios.create({
    baseURL: baseURL + '/api/v1',
    headers: {
      Authorization: `Bearer ${token}`
    }
  })

  api.interceptors.response.use(
    response => {
      return response
    },
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        if (error.response.data?.code_error === 'token.login.expired') {
          const refresh_token = getRefreshToken(ctx)
          const originalConfig = error.config

          if (!isRefreshing) {
            isRefreshing = true

            api
              .post('/refresh_token', { refresh_token })
              .then(response => {
                const { token, refresh_token } = response.data

                // pay attention
                setTokenCookie(token)

                // pay attention
                setRefreshTokenCookie(refresh_token)

                api.defaults.headers.Authorization = `Bearer ${token}`
                failedRequestQueue.forEach(request => request.onSuccess(token))
                failedRequestQueue = []
              })
              .catch(err => {
                failedRequestQueue.forEach(request => request.onFailure(err))
                failedRequestQueue = []

                if (process.browser) {
                  signOut()
                } else {
                  //
                }
              })
              .finally(() => {
                isRefreshing = false
              })
          }

          return new Promise((resolve, reject) => {
            failedRequestQueue.push({
              onSuccess: (token: string) => {
                originalConfig.headers.Authorization = `Bearer ${token}`
                resolve(api(originalConfig))
              },
              onFailure: (err: AxiosError) => {
                reject(err)
              }
            })
          })
        } else {
          if (process.browser) {
            signOut('/login')
          } else {
            return Promise.reject(new AuthtokenError())
          }
        }
      }
      if (error.response?.status === 403) {
        if (process.browser) {
          Router.push('/admin/home')
        } else {
          return Promise.reject(new AuthForbiddenError())
        }
      }
      return Promise.reject(error)
    }
  )

  return api
}
